import { useEffect, useRef, useState, useMemo } from "react";
import Draggable from "react-draggable";
import ReactMarkdown from "react-markdown";
import { l, prefilledQuestions } from "./constants";
import { arrow_down, Avi_Icon, chatbot } from "../assets";
import { SendIcon } from "../icons";
import toast from "react-hot-toast";
import { fetcher } from "../utils/fetcher";
import {
  ANALYTICS_KEY,
  BASEURL2,
  DEPT,
  META_DATA,
  SAVE_CHAT,
} from "../utils/constants";
import ConfirmationModal from "./confirmationModal";
import ChatBotForm from "./chatBotForm";
import { useLocation } from "react-router-dom";
import { setDate } from "date-fns";
import FeedbackForm from "./feedbackForm";
import LangusgeSwitcher from "./languageSwitcher";
import { encryptData } from "../utils/security";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState("chat");
  const [followUp, setFollowUp] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showLikedFeedback, setShowLikedFeedback] = useState(false);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [showFeedbackForm, setShowFeedbackForm] = useState(null);
  const [currentLikedMessage] = useState(null);
  const [lang, setLang] = useState("english");
  const [isScrollable, setIsScrollable] = useState(false);
  const containerRef = useRef(null);
  const { pathname } = useLocation("");

  const [assistantData, setAssistantData] = useState(null);

  const saveChat = async (d) => {
    const data = encryptData({
      organizationId: META_DATA.org_id,
      client: META_DATA.assistant_id,
      conversationTitle: `Conversation-${d.followup_id}`,
      conversationId: d.followup_id,
      data: d,
    });

    try {
      await fetch(SAVE_CHAT, {
        method: "POST",
        body: JSON.stringify({ encryptedData: data }),
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": ANALYTICS_KEY,
        },
      });
      // Process the response data
    } catch (error) {
      console.log(error);

      // Handle errors, including token expiration
    }
  };
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    const form = new FormData(e.target);
    const formObject = Object.fromEntries(form);
    const payload = {
      ...formObject,
      dept: DEPT,
      phone: phone,
      date: startDate,
      time: startDate,
      country_region: country,
    };

    try {
      await fetcher(`${BASEURL2}chat/chatbot/request`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });

      setFormSubmitted(true);
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });

      setIsScrollable(false);
    }
  };

  const handleSend = async (que) => {
    const question = inputValue.trim() ? inputValue.trim() : que;
    if (question) {
      const newMessage = { sender: "user", text: question };
      setMessages([...messages, newMessage]);
      setInputValue("");
      setLoading(true);
      scrollToBottom();
      const response = await onSubmitAssistant(question);
      setLoading(false);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: "bot",
          text: response?.message || "An error occurred.",
          feedback: null,
        },
      ]);
      scrollToBottom();
      setTimeout(() => {
        if (response?.is_form) setAction("call");
      }, 2000);
    }
  };


  const handleFeedbackSubmit = async (feedback) => {
    const feedbacks = [...feedback.reasons, feedback?.additionalFeedback];
    const payload = {
      followup_id: followUp,
      note: feedbacks,
      rating: 0,
    };

    await fetcher(`${BASEURL2}chat/chatbot/feedback`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const handleFetchAssistant = async () => {
    const payload = {
      assistant_id: [META_DATA.assistant_id],
    };

    try {
      const response = await fetcher(`${BASEURL2}assistant/assistant`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": ANALYTICS_KEY,
        },
      });
      setAssistantData(response[0]);
    } catch (err) {
      console.log(err);
    }
  };

  const onSubmitAssistant = async (question) => {
    if (question.length < 1) return;
    try {
      const payload = {
        query: question,
        followup_id: followUp,
        language: lang,
        collection_name: assistantData?.chat_assistant_collection_id,
        index_name: assistantData?.chat_assistant_index_id,
        ...META_DATA,
      };

      const fetchResponse = await fetcher(`${BASEURL2}assistant/chat/chatbot`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": ANALYTICS_KEY,
        },
      });
      if (fetchResponse.failure) throw JSON.parse(fetchResponse.message).error;
      saveChat(fetchResponse);
      setFollowUp(fetchResponse?.followup_id);

      return fetchResponse?.is_form
        ? {
            message: l[lang]?.infoTexts?.scheduleMessage,
            is_form: true,
          }
        : { message: fetchResponse.response, is_form: false };
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCloseChat = () => {
    setShowModal(true);
  };

  const confirmCloseChat = () => {
    toggleChat();
    setMessages([]);
    setAction("chat");
    setPhone("");
    setCountry("");
    setDate("");
    setShowModal(false);
    setSubmitting(false);
    setFollowUp("");
  };

  const cancelCloseChat = () => {
    setShowModal(false);
  };

  const checkIfScrollable = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const atBottom = scrollTop >= scrollHeight - clientHeight - 1;
      setIsScrollable(!atBottom);
    }
  };

  useEffect(() => {
    checkIfScrollable();

    const handleScroll = () => {
      if (containerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const atBottom = scrollTop >= scrollHeight - clientHeight - 1;

        setIsScrollable(!atBottom);
      }
    };

    const currentContainerRef = containerRef.current;
    if (currentContainerRef) {
      currentContainerRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentContainerRef) {
        currentContainerRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [messages, messages.length, isScrollable, inputValue]);

  const isMobile = window.innerWidth <= 768;
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLikedFeedback(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [showLikedFeedback]);

  useMemo(() => {
    handleFetchAssistant();
  }, []);
  const ChatWindow = (
    <div className="chat-window">
      <div className="chat-header border-b border-[#E3E3E3]">
        <div className="flex gap-3 items-center">
          <img src={Avi_Icon} alt="avi icon" className="w-[37px]" />
          <div>
            <h3 className="text-sm font-extrabold text-black">Lara</h3>
            <p className="text-sm text-black">AI Assistant</p>
          </div>
        </div>

        <div className="flex gap-[30px] items-center">
          <div className="flex gap-2 items-center">
            <button
              className={`p-1.5 ${
                action === "chat" ? "bg-[#EAF3FF] rounded-[4px]" : ""
              }`}
              onClick={() => setAction("chat")}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.5 8.746C13.5 8.94491 13.421 9.13568 13.2803 9.27633C13.1397 9.41698 12.9489 9.496 12.75 9.496H6.75C6.55109 9.496 6.36032 9.41698 6.21967 9.27633C6.07902 9.13568 6 8.94491 6 8.746C6 8.54709 6.07902 8.35632 6.21967 8.21567C6.36032 8.07502 6.55109 7.996 6.75 7.996H12.75C12.9489 7.996 13.1397 8.07502 13.2803 8.21567C13.421 8.35632 13.5 8.54709 13.5 8.746ZM12.75 10.996H6.75C6.55109 10.996 6.36032 11.075 6.21967 11.2157C6.07902 11.3563 6 11.5471 6 11.746C6 11.9449 6.07902 12.1357 6.21967 12.2763C6.36032 12.417 6.55109 12.496 6.75 12.496H12.75C12.9489 12.496 13.1397 12.417 13.2803 12.2763C13.421 12.1357 13.5 11.9449 13.5 11.746C13.5 11.5471 13.421 11.3563 13.2803 11.2157C13.1397 11.075 12.9489 10.996 12.75 10.996ZM19.5 10.246C19.5004 11.9293 19.0649 13.584 18.236 15.0491C17.4072 16.5142 16.2131 17.7398 14.77 18.6065C13.327 19.4732 11.6841 19.9515 10.0014 19.9949C8.31863 20.0383 6.6533 19.6453 5.1675 18.8541L1.97531 19.9182C1.71102 20.0063 1.4274 20.0191 1.15624 19.9551C0.885089 19.8911 0.637113 19.7529 0.44011 19.5559C0.243108 19.3589 0.104864 19.1109 0.0408727 18.8398C-0.0231183 18.5686 -0.0103272 18.285 0.0778122 18.0207L1.14187 14.8285C0.446389 13.5209 0.0579347 12.0721 0.0059975 10.5919C-0.0459397 9.11177 0.240005 7.63925 0.842128 6.28613C1.44425 4.933 2.34672 3.73482 3.48105 2.78256C4.61537 1.83029 5.95173 1.14895 7.38869 0.790259C8.82565 0.43157 10.3254 0.404957 11.7742 0.71244C13.223 1.01992 14.5827 1.65342 15.7501 2.56485C16.9175 3.47627 17.8619 4.64168 18.5116 5.97259C19.1614 7.3035 19.4994 8.76495 19.5 10.246ZM18 10.246C17.9996 8.98049 17.7082 7.73203 17.1481 6.5972C16.588 5.46237 15.7744 4.4716 14.7701 3.70153C13.7659 2.93147 12.5979 2.40276 11.3567 2.1563C10.1154 1.90985 8.83405 1.95226 7.61178 2.28025C6.38951 2.60824 5.25909 3.21302 4.30796 4.04781C3.35682 4.88259 2.61049 5.92499 2.12668 7.09437C1.64288 8.26375 1.43458 9.52876 1.5179 10.7915C1.60122 12.0543 1.97393 13.281 2.60719 14.3766C2.66034 14.4686 2.69334 14.5708 2.704 14.6765C2.71467 14.7822 2.70276 14.889 2.66906 14.9898L1.5 18.496L5.00625 17.3269C5.08262 17.3009 5.16275 17.2876 5.24344 17.2876C5.37516 17.2878 5.5045 17.3227 5.61844 17.3888C6.87263 18.1145 8.29581 18.497 9.74479 18.4979C11.1938 18.4988 12.6174 18.1181 13.8725 17.394C15.1276 16.67 16.1699 15.6281 16.8945 14.3733C17.619 13.1185 18.0003 11.695 18 10.246Z"
                  fill="#0073FF"
                />
              </svg>
            </button>
          </div>
          <LangusgeSwitcher lang={lang} setLang={setLang} />
          <button onClick={handleCloseChat} className="cursor-pointer">
            <svg
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.2806 13.7194C15.3502 13.789 15.4055 13.8718 15.4432 13.9628C15.4809 14.0539 15.5003 14.1514 15.5003 14.25C15.5003 14.3485 15.4809 14.4461 15.4432 14.5372C15.4055 14.6282 15.3502 14.7109 15.2806 14.7806C15.2109 14.8503 15.1281 14.9056 15.0371 14.9433C14.9461 14.981 14.8485 15.0004 14.7499 15.0004C14.6514 15.0004 14.5538 14.981 14.4628 14.9433C14.3717 14.9056 14.289 14.8503 14.2193 14.7806L7.99993 8.5603L1.78055 14.7806C1.63982 14.9213 1.44895 15.0004 1.24993 15.0004C1.05091 15.0004 0.860034 14.9213 0.719304 14.7806C0.578573 14.6399 0.499512 14.449 0.499512 14.25C0.499512 14.051 0.578573 13.8601 0.719304 13.7194L6.93962 7.49999L0.719304 1.28061C0.578573 1.13988 0.499512 0.949013 0.499512 0.74999C0.499512 0.550967 0.578573 0.360095 0.719304 0.219365C0.860034 0.0786343 1.05091 -0.000427246 1.24993 -0.000427246C1.44895 -0.000427246 1.63982 0.0786343 1.78055 0.219365L7.99993 6.43968L14.2193 0.219365C14.36 0.0786343 14.5509 -0.00042725 14.7499 -0.000427246C14.949 -0.000427242 15.1398 0.0786343 15.2806 0.219365C15.4213 0.360095 15.5003 0.550967 15.5003 0.74999C15.5003 0.949013 15.4213 1.13988 15.2806 1.28061L9.06024 7.49999L15.2806 13.7194Z"
                fill="#E32119"
              />
            </svg>
          </button>
        </div>
      </div>
      <div className="chat-intro">
        {!action && (
          <>
            <h1 className="chat-intro-message">
              {l[lang]?.infoTexts?.moreInfo}
            </h1>

            <div className="flex justify-between items-center gap-[16px] max-w-[398px] mx-auto">
              {l[lang]?.actions?.map((action) => (
                <button
                  onClick={() => setAction(action.action)}
                  className="action-button"
                  key={action.action}
                >
                  <img src={action.icon} alt={action.action} />
                  <p>{action.desc}</p>
                </button>
              ))}
            </div>
          </>
        )}
        {action === "chat" && messages?.length === 0 && (
          <div className="prefilled-questions">
            <p className="text-[#737373] text-sm mb-5 leading-[150%]">
              Welcome back to Analytics Intelligence! I'm Lara, your AI
              Assistant. Anything you'd like to know about our services today?
            </p>
            {prefilledQuestions(pathname, lang)?.map((que) => (
              <button
                className="prefilled-que"
                key={que}
                onClick={() => {
                  handleSend(que);
                }}
              >
                {que}
              </button>
            ))}
          </div>
        )}

        {action === "call" && (
          <>
            {!formSubmitted && (
              <div className="px-5">
                <h1 className="text-base font-extrabold text-[#010127]">
                  [AI] Customer Care Representative
                </h1>
                <p className="chat-intro-message">
                  {l[lang]?.infoTexts?.scheduleCall}
                </p>
              </div>
            )}
            <ChatBotForm
              submitted={formSubmitted}
              handleSubmit={handleFormSubmit}
              reset={setFormSubmitted}
              submitting={submitting}
              phone={phone}
              setPhone={setPhone}
              setCountry={setCountry}
              startDate={startDate}
              setStartDate={setStartDate}
              language={lang}
              onCancel={() => setAction("chat")}
            />
          </>
        )}
      </div>
      {action === "chat" && messages?.length > 0 && (
        <div className="chat-content w-full">
          <div ref={containerRef} className="messages">
            {messages?.map((message, index) => (
              <div key={index} className={`message ${message.sender}`}>
                {message.sender === "bot" ? (
                  <>
                    <ReactMarkdown className="markdown">
                      {message.text}
                    </ReactMarkdown>
                    {/* Show thank you message only if liked */}
                    {message.feedback === "liked" &&
                      index === currentLikedMessage && (
                        <p
                          className={
                            showLikedFeedback ? "block feedback-msg" : "hidden"
                          }
                        >
                          {l[lang]?.infoTexts?.feedbackThankYou}{" "}
                        </p>
                      )}

                    {/* Show feedback form only if disliked */}
                    {message.feedback === "disliked" &&
                      index === showFeedbackForm && (
                        <FeedbackForm
                          onSubmit={handleFeedbackSubmit}
                          setShowFeedback={setShowFeedbackForm}
                          language={lang}
                        />
                      )}
                  </>
                ) : (
                  message.text
                )}
              </div>
            ))}
            {loading && (
              <div className=" loading">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            )}
          </div>
          {messages.length > 1 && isScrollable && (
            <button
              className="sticky arrow-down mx-auto"
              onClick={scrollToBottom}
            >
              <img src={arrow_down} alt="arrow down" />
            </button>
          )}
        </div>
      )}
      {action === "chat" && (
        <div className="input-area">
          <textarea
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Ask a question"
            rows={1}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
          ></textarea>
          <button onClick={handleSend} disabled={!inputValue.trim()}>
            <SendIcon />
          </button>
        </div>
      )}

      <div className="border-t border-[#E3E3E3] mt-3 py-3 mx-5">
        <p className="text-xs text-[#737373] leading-[150%]">
          By interacting with this Al chat, you agree that Analytics
          Intelligence may collect data as outlined in our{" "}
          <span className="font-medium text-[#0073FF]">Privacy Policy</span>.
        </p>
      </div>
    </div>
  );

  return (
    <>
      <div className="chatbot-container">
        {!isOpen && (
          <div className="cursor-pointer" onClick={toggleChat}>
            <img src={chatbot} alt="Chat Icon" />
          </div>
        )}
      </div>
      {isMobile ? (
        <div className="chatbot-container">
          {isOpen && ChatWindow}
          {showModal && (
            <ConfirmationModal
              onConfirm={confirmCloseChat}
              onCancel={cancelCloseChat}
            />
          )}
        </div>
      ) : (
        <Draggable>
          <div className="chatbot-container">
            {isOpen && ChatWindow}
            {showModal && (
              <ConfirmationModal
                onConfirm={confirmCloseChat}
                onCancel={cancelCloseChat}
              />
            )}
          </div>
        </Draggable>
      )}
    </>
  );
};

export default Chatbot;
